// Endpoints RTK Query da feature inbox. Tipos manuais espelhando contrato com o
// back (readme §11 janela 24h + §12 cursor + handoff Onda 2).
// FIXME: migrar pra paths['/v1/conversations']... quando openapi-typescript regerar.
import { api } from '@/lib/api';
import type { AvatarTone } from '@/components/ui/Avatar';
import type { CursorPage } from '@/features/stores';
import type { CustomerTier } from '@/features/contacts/utils/tier';
import { selectCurrentUser } from '@/features/auth';

export type ConversationStatus = 'IA' | 'QUEUE' | 'ASSIGNED' | 'ACTIVE' | 'DONE';
export type ConversationScope = 'queue' | 'mine' | 'team' | 'store';

/** Motivo de conclusão do atendimento (RF-05) — espelha o enum do back. */
export type ConclusionReason = 'SALE' | 'NO_SALE' | 'NOT_ANSWERED' | 'PICKUP' | 'PRODUCTION';

/** Rótulos pt-BR dos motivos de conclusão (RF-05) — usados no modal e no painel. */
export const CONCLUSION_REASON_LABELS: Record<ConclusionReason, string> = {
  SALE: 'Venda realizada',
  NO_SALE: 'Venda não realizada',
  NOT_ANSWERED: 'Não atendido',
  PICKUP: 'Retirada',
  PRODUCTION: 'Produção',
};
export type MessageDirection = 'INBOUND' | 'OUTBOUND';
export type MessageSource = 'AI' | 'HUMAN' | 'SYSTEM';
export type MessageType =
  | 'TEXT'
  | 'IMAGE'
  | 'AUDIO'
  | 'VIDEO'
  | 'FILE'
  | 'QUOTE'
  | 'HANDOFF'
  | 'TEMPLATE'
  | 'STICKER'
  | 'CONTACTS';
export type MessageStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

export interface ContactSummary {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  company: string | null;
  tags: string[];
  avatarTone: AvatarTone;
  /** RF-02: categoria vigente (ano corrente). null = sem categoria/anonimizado. */
  tier: CustomerTier | null;
}

export interface AssigneeSummary {
  id: string;
  name: string;
  avatarTone: AvatarTone;
}

export interface ConversationAutomation {
  enabled: boolean;
  toggledBy: string | null;
  toggledAt: string | null;
}

export interface ConversationSummary {
  id: string;
  contact: ContactSummary;
  storeId: string;
  status: ConversationStatus;
  assignedTo: AssigneeSummary | null;
  automation: ConversationAutomation;
  lastMessagePreview: string | null;
  lastMessageAt: string;
  unreadCount: number;
  /** ISO — quando a janela 24h fecha (readme §11). */
  windowClosesAt: string | null;
  /**
   * Conversa que veio do despejo de histórico do WhatsApp (o onboarding do número), não de
   * um atendimento feito aqui. Combinado com a janela vencida, troca o rótulo de status
   * por "Onboarding": não existe atendimento a fazer sobre conversa de meses atrás.
   */
  importado?: boolean;
  /** RF-13: ISO de quando ficou "Não atendida" (24h sem nenhuma resposta). null =
   *  atendida ou cliente retornou. Estado interino — não é conclusão. */
  unansweredAt: string | null;
  /** MET-15: etapa do funil. null = conversa ainda não classificada. */
  stage: ConversationStageRef | null;
  /** MET-17: origem do lead, com a PROCEDÊNCIA junto. */
  leadSource: ConversationLeadSourceRef | null;
}

/** MET-15. */
export interface ConversationStageRef {
  id: string;
  label: string;
  kind: 'OPEN' | 'WON' | 'LOST' | 'NEUTRAL';
}

/**
 * MET-17. A procedência é o que evita o atendente "corrigir" uma origem que o
 * sistema acertou: `AUTO_*` mostra "Detectada automaticamente", `MANUAL` mostra
 * quem ajustou e quando. Sem isso, a edição manual degrada o dado em vez de
 * melhorá-lo.
 */
export interface ConversationLeadSourceRef {
  id: string;
  label: string;
  origin: 'AUTO_REFERRAL' | 'AUTO_TEXT' | 'AUTO_CAMPAIGN' | 'MANUAL' | 'UNKNOWN';
  setByName: string | null;
  setAt: string | null;
}

/** Sessão anterior do mesmo contato — card no "Histórico recente" do painel direito. */
export interface RecentConversation {
  id: string;
  status: ConversationStatus;
  createdAt: string;
  lastMessageAt: string;
  messageCount: number;
  preview: string | null;
  /** Tipo da última mensagem — front mostra "🎤 Áudio"/"📷 Imagem" etc. */
  previewType: MessageType | null;
  /** Motivo da conclusão (RF-05) — só presente quando a sessão foi concluída. */
  conclusionReason: ConclusionReason | null;
  /** Valor da venda em centavos (RF-05) — só quando conclusionReason === 'SALE'. */
  saleValueCents: number | null;
  /**
   * Atendimento que veio do despejo de histórico do WhatsApp (o onboarding do número),
   * não de alguém atendido aqui. Marcado na régua para o atendente não ler conversa
   * antiga da loja como se fosse trabalho feito no sistema.
   */
  importado: boolean;
}

/** Status do orçamento (RF-13/15) — espelha o enum QuoteStatus do back. Só DRAFT e
 *  APPROVED governam a edição no front (DRAFT = editável em revisão; APPROVED =
 *  concluído, só gestor corrige). */
export type QuoteStatus = 'DRAFT' | 'SENT' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

/** Item do orçamento gerado, exibido no painel ("Orçamento gerado"). */
export interface QuoteLine {
  /** RF-13: id do item — necessário pro replace-set do PATCH (item sem id = novo). */
  id: string;
  description: string;
  qty: number;
  unitPriceCents: number;
  totalCents: number;
  /** RF-04: id do produto do catálogo quando o item foi vinculado; null = texto livre. */
  catalogProductId: string | null;
}

export interface ConversationQuote {
  /** RF-13: id do orçamento — alvo do PATCH /quotes/:id. */
  id: string;
  /** RF-13/15: DRAFT = editável em revisão; APPROVED = concluído. */
  status: QuoteStatus;
  /** RF-13: lock otimista — enviado no PATCH; 409 se divergir. */
  version: number;
  totalCents: number;
  createdAt: string;
  items: QuoteLine[];
}

/** Item enviado no PATCH de orçamento (RF-13/15). Sem `id` = item novo (replace-set);
 *  item existente ausente do array = removido. */
export interface QuoteItemInput {
  id?: string;
  description: string;
  qty: number;
  unitPriceCents: number;
  totalCents: number;
  /** RF-04: presente = item do catálogo; null/ausente = texto livre. */
  catalogProductId?: string | null;
}

/** RF-05: uma chamada LLM registrada nesta conversa (rastreabilidade do custo). */
export interface ConversationCostEntry {
  task: string;
  provider: string;
  model: string;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  costUsd: number;
  success: boolean;
  fallbackUsed: boolean;
  /** ISO. */
  createdAt: string;
}

/** RF-05: custo agregado por tarefa (ex.: 'reply', 'analysis'). */
export interface ConversationCostTask {
  task: string;
  calls: number;
  costUsd: number;
}

/** RF-05: custo de IA de uma conversa — totalizador + breakdown + registros. */
export interface ConversationCost {
  totalUsd: number;
  calls: number;
  byTask: ConversationCostTask[];
  entries: ConversationCostEntry[];
}

/** Args das mutations de edição de orçamento (DRAFT e concluído). `conversationId`
 *  não vai no body — serve só pra invalidar a tag da conversa correta. */
export interface UpdateQuoteArgs {
  quoteId: string;
  conversationId: string;
  version: number;
  items: QuoteItemInput[];
}

/** Tipo de evento no log do atendimento (RF-14) — espelha o enum do back. */
export type ConversationEventType =
  | 'HANDOFF'
  | 'CLAIMED'
  | 'RELEASED'
  | 'ASSIGNED'
  | 'STARTED'
  | 'DONE'
  | 'REOPENED'
  | 'AI_FALLBACK'
  // RF-28: gestor participou (enviou msg sem assumir) / assumiu conversa da fila.
  | 'GESTOR_PARTICIPATED'
  | 'GESTOR_ASSUMED';

/** Uma linha do histórico cumulativo do atendimento (RF-14). */
export interface ConversationEvent {
  id: string;
  type: ConversationEventType;
  actorName: string | null;
  targetName: string | null;
  /** ISO. */
  createdAt: string;
}

export interface ConversationDetail extends ConversationSummary {
  createdAt: string;
  updatedAt: string;
  version: number;
  /** Últimas conversas do contato (exceto a atual), pra "Histórico recente". */
  recentConversations: RecentConversation[];
  /** Horários do fluxo pra timeline "Status do atendimento". */
  queuedAt: string | null;
  claimedAt: string | null;
  /** Orçamento gerado na conversa (itens + total), ou null. */
  quote: ConversationQuote | null;
  /** Motivo da conclusão (RF-05) — só presente quando a conversa foi concluída. */
  conclusionReason: ConclusionReason | null;
  /** Valor da venda em centavos (RF-05) — só quando conclusionReason === 'SALE'. */
  saleValueCents: number | null;
}

export type ConversationOutcome = 'CONVERTED' | 'LOST' | 'IN_PROGRESS' | 'ABANDONED';

/** Análise pós-conversa da IA (LLM-as-a-Judge). Só existe pra conversas DONE. */
export interface ConversationAnalysis {
  conversationId: string;
  /** Nota de qualidade 0-100. */
  score: number;
  outcome: ConversationOutcome;
  issues: string[];
  suggestions: string[];
  highlights: string[];
  createdAt: string;
}

/**
 * Resposta do botão "Resumir conversa" (POST /conversations/:id/summary).
 *
 * ⚠️ `ConversationSummary` (sem o "AI") já é o item da LISTA de conversas neste
 * mesmo arquivo — daí o nome mais longo.
 */
export interface ConversationAiSummary {
  summary: string;
  /** ISO — o texto sem a data não diz se é de agora ou de 30 mensagens atrás. */
  generatedAt: string;
}

export interface QuoteItem {
  description: string;
  quantity: number;
  unitCents: number;
  totalCents: number;
}

export interface QuotePayload {
  items: QuoteItem[];
  shippingCents: number;
  totalCents: number;
  validUntil: string | null;
}

/** Um cartão de contato compartilhado (type=CONTACTS), já normalizado pelo back. */
export interface SharedContact {
  name: string;
  phones: Array<{ phone: string; waId: string | null; type: string | null }>;
  emails: string[];
  org: string | null;
}

/** Metadados de mídia (image/audio/file) — vêm de Message.metadata. */
export interface MediaMetadata {
  mimeType?: string;
  filename?: string;
  size?: number;
  durationSec?: number;
  /** STICKER: figurinha animada (webp animado toca sozinho no <img>). */
  animated?: boolean;
  sha256?: string;
  /** CONTACTS: os cartões recebidos/enviados. `text` traz só o resumo. */
  contacts?: SharedContact[];
}

/**
 * Mensagem citada (resposta do WhatsApp). Ausente/null = não é resposta;
 * `{ unavailable: true }` = é resposta, mas o original não está no banco (ex.:
 * anterior ao onboarding do número).
 */
export interface QuotedMessage {
  id: string;
  direction: MessageDirection;
  source: 'CUSTOMER' | MessageSource;
  authorName: string | null;
  type: MessageType;
  /** Trecho (até 160 caracteres). */
  text: string | null;
  mediaUrl: string | null;
}
export type MessageReplyTo = QuotedMessage | { unavailable: true };

export function isQuoteUnavailable(r: MessageReplyTo): r is { unavailable: true } {
  return 'unavailable' in r && r.unavailable === true;
}

export type ReactionActor = 'CUSTOMER' | 'STORE';
export interface MessageReaction {
  actor: ReactionActor;
  emoji: string;
  authorId: string | null;
}

/** Contato enviado pelo atendente (menu Anexar > Contato): só o número, nome opcional. */
export interface OutgoingContact {
  phone: string;
  name?: string;
}

export interface InboxMessage {
  id: string;
  conversationId: string;
  clientMessageId: string | null;
  direction: MessageDirection;
  source: MessageSource;
  type: MessageType;
  text: string | null;
  /** URL pronta do proxy de mídia (/api/v1/media/:id?t=…) pra image/audio/file. */
  mediaUrl: string | null;
  /** Metadados da mídia (mime/filename/size) quando type=IMAGE/AUDIO/FILE. */
  metadata?: MediaMetadata | null;
  templateName: string | null;
  /** Payload estruturado pra type=QUOTE. */
  quote: QuotePayload | null;
  /**
   * Autor humano quando source=HUMAN (o atendente que enviou pelo sistema).
   *
   * 🔴 Aqui havia um `authorName: string | null` que **o servidor nunca mandou**: a
   * tabela `Message` só tem `authorId`, e o nome vem no objeto aninhado
   * (`MESSAGE_INCLUDE` seleciona `{ id, name, avatarTone }`). O campo plano era sempre
   * `undefined`, a bolha caía no fallback e TODA mensagem enviada aparecia como "Você",
   * inclusive na conversa de outro atendente. Testes passavam porque os fixtures
   * inventavam o campo. O shape abaixo é o que a API realmente devolve.
   *
   * Nulo em três casos legítimos: IA, mensagem de sistema e saída sem atendente
   * (celular da loja e histórico importado) — a bolha rotula pela origem.
   */
  authorId: string | null;
  author: AssigneeSummary | null;
  status: MessageStatus;
  createdAt: string;
  /** Mensagem citada quando é uma resposta. Ausente em payloads antigos. */
  replyTo?: MessageReplyTo | null;
  /** Uma reação por lado (cliente e loja). Opcional: bolha otimista nasce sem. */
  reactions?: MessageReaction[];
}

export interface ListConversationsArgs {
  scope: ConversationScope;
  storeId?: string;
  status?: ConversationStatus;
  cursor?: string;
}

/**
 * Tamanho da página da thread. Sem `limit` o back devolve 20 (teto 100), e a tela só
 * mostrava as 20 últimas: em conversa movimentada, uma imagem de uma hora antes sumia
 * do chat sem aviso. Quem pede as páginas anteriores é o `useConversationMessages`.
 */
export const MESSAGES_PAGE_SIZE = 50;

export interface ListMessagesArgs {
  conversationId: string;
  cursor?: string;
  limit?: number;
  /**
   * Busca do cursor até esta mensagem (inclusive) mais uma página anterior: é o que
   * a thread pede ao clicar numa citação cujo original ainda não foi carregado.
   */
  untilId?: string;
}

/**
 * Campos de resposta comuns a todo envio. `replyToMessageId` vai ao servidor; `replyTo`
 * só alimenta a bolha otimista (a citação aparece antes da resposta do POST).
 */
interface ReplyArgs {
  replyToMessageId?: string;
  replyTo?: QuotedMessage;
}

export interface SendMessageArgs extends ReplyArgs {
  conversationId: string;
  clientMessageId: string;
  /** Opcional: não é exigido para type=TEMPLATE. */
  text?: string;
  type?: MessageType;
  mediaUrl?: string;
  templateName?: string;
  /** Contexto extra do envio (ex.: { language, variables } para templates). */
  metadata?: Record<string, unknown>;
}

export interface SendContactsArgs extends ReplyArgs {
  conversationId: string;
  clientMessageId: string;
  contacts: OutgoingContact[];
}

/** Figurinha: reaproveita uma recente da loja OU sobe um arquivo (o back converte). */
export type SendStickerArgs = ReplyArgs & {
  conversationId: string;
  clientMessageId: string;
} & ({ stickerMessageId: string; previewUrl: string } | { file: File });

export interface SetReactionArgs {
  conversationId: string;
  messageId: string;
  /** '' remove a reação da loja. */
  emoji: string;
}

export interface RecentSticker {
  messageId: string;
  mediaUrl: string;
  animated: boolean;
}

/** Template WhatsApp APROVADO (HSM) — de GET /conversations/:id/templates. */
export interface WhatsappTemplate {
  name: string;
  language: string;
  category: string;
  /** Corpo (BODY) do template, com placeholders {{1}}, {{2}}, … quando houver. */
  bodyText: string;
  variableCount: number;
}

/** Resposta da tela de Templates — lista + motivo de vir vazia. */
export interface TemplatesResponse {
  items: WhatsappTemplate[];
  connected: boolean;
  reason?: 'NOT_CONNECTED' | 'TOKEN_INVALID' | 'FETCH_FAILED';
}

export interface SendMediaArgs extends ReplyArgs {
  conversationId: string;
  clientMessageId: string;
  type: 'IMAGE' | 'AUDIO' | 'VIDEO' | 'FILE';
  /** Binário enviado em multipart — File (anexo) ou Blob (áudio gravado). */
  file: File | Blob;
  /** Nome do arquivo no multipart (obrigatório para Blob de áudio). */
  filename?: string;
  /** Legenda opcional para imagem/arquivo. */
  caption?: string;
  /** Duração do áudio em segundos (type=AUDIO). */
  durationSec?: number;
}

export interface AssignArgs {
  conversationId: string;
  userId: string;
}

export interface UpdateStatusArgs {
  conversationId: string;
  status: ConversationStatus;
  /** Optimistic locking — o back exige a versão atual da conversa (DTO). */
  version: number;
  /**
   * Motivo da conclusão (RF-05) — obrigatório quando status==='DONE'. Nunca 'SALE'
   * por aqui (venda vai pelo endpoint /sale via finalizeSale).
   */
  reason?: ConclusionReason;
  /** MET-16: obrigatório quando `reason === 'NO_SALE'` — o back devolve
   *  422 `LOSS_REASON_REQUIRED` sem ele, no mesmo padrão do `REASON_REQUIRED`. */
  lossReasonId?: string;
}

export interface ToggleAutomationArgs {
  conversationId: string;
  enabled: boolean;
}

export interface CreateConversationArgs {
  contactId: string;
  storeId: string;
  /**
   * Número de WhatsApp por onde a conversa sai. Ausente = o principal da loja.
   * A conversa fica PRESA ao escolhido: toda resposta posterior sai por ele, porque
   * a janela de 24h da Meta é por par (número, cliente).
   */
  storeWhatsappNumberId?: string;
  /** Opcional — quando presente, back faz auto-claim e status vira ACTIVE. */
  firstMessage?: string;
  /**
   * Primeira mensagem por template APROVADO — padrão do modal (conversa iniciada
   * pela loja só entrega HSM aprovado, não texto livre). Exclusivo com firstMessage.
   */
  template?: { name: string; language?: string; variables?: Record<string, string> };
  /** Obrigatório quando firstMessage/template presente (UUID v4 do cliente). */
  clientMessageId?: string;
}

/**
 * Autor das bolhas otimistas: o usuário logado, no mesmo shape que o servidor devolve.
 *
 * Sem isto a mensagem que ainda está subindo nasceria SEM autor, e a bolha a rotularia
 * como "Loja" (a regra de quem não tem atendente gravado) até a resposta chegar.
 * `getState()` é genérico aqui para não criar ciclo de import — mesmo recurso de
 * `features/notifications/sound-listeners.ts`.
 */
function autorLogado(getState: () => unknown): AssigneeSummary | null {
  const user = selectCurrentUser(getState() as Parameters<typeof selectCurrentUser>[0]);
  return user ? { id: user.id, name: user.name, avatarTone: user.avatarTone } : null;
}

/** Bolha PENDING de um envio: autor logado, citação da resposta e os campos do tipo. */
function mensagemOtimista(
  arg: { conversationId: string; clientMessageId: string; replyTo?: QuotedMessage },
  getState: () => unknown,
  fields: Partial<InboxMessage> & Pick<InboxMessage, 'type'>,
): InboxMessage {
  const eu = autorLogado(getState);
  return {
    id: `optimistic-${arg.clientMessageId}`,
    conversationId: arg.conversationId,
    clientMessageId: arg.clientMessageId,
    direction: 'OUTBOUND',
    source: 'HUMAN',
    text: null,
    mediaUrl: null,
    templateName: null,
    quote: null,
    authorId: eu?.id ?? null,
    author: eu,
    status: 'PENDING',
    createdAt: new Date().toISOString(),
    replyTo: arg.replyTo ?? null,
    reactions: [],
    ...fields,
  };
}

// O `dispatch` do ciclo de vida das mutations é tipado por endpoint; este helper serve a
// todos, então aceita qualquer action (runtime idêntico, igual ao ws-listeners.ts).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseDispatch = (action: any) => any;

/**
 * Empurra a bolha otimista no fim da thread e a troca pela mensagem do servidor (mesmo
 * clientMessageId). Se o envio falha, desfaz: quem chamou levanta o toast.
 */
async function envioOtimista(
  dispatch: LooseDispatch,
  queryFulfilled: Promise<{ data: InboxMessage }>,
  optimistic: InboxMessage,
): Promise<void> {
  const { conversationId, clientMessageId } = optimistic;
  const patch = dispatch(
    inboxApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
      draft.items.push(optimistic);
    }),
  ) as { undo: () => void };
  try {
    const { data } = await queryFulfilled;
    dispatch(
      inboxApi.util.updateQueryData('listMessages', { conversationId }, (draft) => {
        const idx = draft.items.findIndex((m) => m.clientMessageId === clientMessageId);
        if (idx >= 0) draft.items[idx] = data;
        else draft.items.push(data);
      }),
    );
  } catch {
    patch.undo();
  }
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export const inboxApi = api.injectEndpoints({
  endpoints: (build) => ({
    listConversations: build.query<CursorPage<ConversationSummary>, ListConversationsArgs>({
      query: ({ scope, storeId, status, cursor }) =>
        `/conversations${qs({ scope, storeId, status, cursor })}`,
      // Back devolve { data, pageInfo: { nextCursor, hasMore } } (envelope canônico
      // do readme §12). Front consome { items, nextCursor } — sem isso `items` vira
      // undefined e merge/providesTags quebram (lista não carrega). Igual stores/api.ts.
      transformResponse: (raw: {
        data: ConversationSummary[];
        pageInfo: { nextCursor: string | null; hasMore: boolean };
      }): CursorPage<ConversationSummary> => ({
        items: raw.data,
        nextCursor: raw.pageInfo.nextCursor,
      }),
      // Merge cursor pages: nova página acrescenta no fim.
      serializeQueryArgs: ({ queryArgs }) => ({
        scope: queryArgs.scope,
        storeId: queryArgs.storeId,
        status: queryArgs.status,
      }),
      merge: (current, incoming, { arg }) => {
        if (!arg.cursor) return incoming;
        const ids = new Set(current.items.map((c) => c.id));
        return {
          items: [...current.items, ...incoming.items.filter((c) => !ids.has(c.id))],
          nextCursor: incoming.nextCursor,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.cursor !== previousArg?.cursor,
      providesTags: (result) =>
        result
          ? [
              { type: 'Conversation' as const, id: 'LIST' },
              ...result.items.map((c) => ({ type: 'Conversation' as const, id: c.id })),
            ]
          : [{ type: 'Conversation' as const, id: 'LIST' }],
    }),
    getConversation: build.query<ConversationDetail, string>({
      query: (id) => `/conversations/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Conversation' as const, id }],
    }),
    // RF-14: histórico cumulativo do atendimento (eventos ASC). Compartilha a tag
    // da conversa → refetch automático quando claim/release/assign a invalidam.
    listConversationEvents: build.query<ConversationEvent[], string>({
      query: (id) => `/conversations/${id}/events`,
      providesTags: (_r, _e, id) => [{ type: 'Conversation' as const, id }],
    }),
    getConversationAnalysis: build.query<ConversationAnalysis, string>({
      query: (id) => `/conversations/${id}/analysis`,
      providesTags: (_r, _e, id) => [{ type: 'Analysis' as const, id }],
    }),
    /**
     * Resumo sob demanda (botão "Resumir conversa"). É `mutation` e não `query`
     * de propósito: cada chamada custa uma ida ao LLM, então quem dispara é o
     * clique — nunca a montagem de um componente.
     */
    summarizeConversation: build.mutation<ConversationAiSummary, string>({
      query: (id) => ({ url: `/conversations/${id}/summary`, method: 'POST' }),
    }),
    // Templates WhatsApp APROVADOS da loja da conversa — o que o TemplatePicker mostra
    // (envio fora da janela 24h / início de conversa só entrega HSM aprovado).
    getTemplates: build.query<TemplatesResponse, string>({
      query: (conversationId) => `/conversations/${conversationId}/templates`,
      providesTags: (_r, _e, id) => [{ type: 'Template' as const, id }],
    }),
    // Mesma lista, escopada por loja — usada no modal "Nova conversa" (sem conversa ainda).
    getStoreTemplates: build.query<TemplatesResponse, string>({
      query: (storeId) => `/stores/${storeId}/whatsapp/templates`,
      providesTags: (_r, _e, id) => [{ type: 'Template' as const, id: `store:${id}` }],
    }),
    listMessages: build.query<CursorPage<InboxMessage>, ListMessagesArgs>({
      query: ({ conversationId, cursor, limit, untilId }) =>
        `/conversations/${conversationId}/messages${qs({ cursor, limit, untilId })}`,
      // Mesmo envelope { data, pageInfo } do back → shape { items, nextCursor }.
      // O back ordena DESC (mais novo primeiro, ideal pra cursor); revertemos pra
      // ASCENDENTE (antigas→novas) pra exibir como chat: antigas no topo, recente
      // embaixo. Consistente com merge (prepend de antigas) + WS/optimistic (push no fim).
      transformResponse: (raw: {
        data: InboxMessage[];
        pageInfo: { nextCursor: string | null; hasMore: boolean };
      }): CursorPage<InboxMessage> => ({
        items: [...raw.data].reverse(),
        nextCursor: raw.pageInfo.nextCursor,
      }),
      serializeQueryArgs: ({ queryArgs }) => ({ conversationId: queryArgs.conversationId }),
      merge: (current, incoming, { arg }) => {
        // Sem cursor = primeira página (mais recentes). Com cursor = página anterior.
        if (!arg.cursor) return incoming;
        const ids = new Set(current.items.map((m) => m.id));
        return {
          // mensagens antigas vão pro topo (lista cronológica ascendente).
          items: [...incoming.items.filter((m) => !ids.has(m.id)), ...current.items],
          nextCursor: incoming.nextCursor,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.cursor !== previousArg?.cursor || currentArg?.untilId !== previousArg?.untilId,
      providesTags: (result, _e, arg) =>
        result
          ? [
              { type: 'Message' as const, id: `CONV:${arg.conversationId}` },
              ...result.items.map((m) => ({ type: 'Message' as const, id: m.id })),
            ]
          : [{ type: 'Message' as const, id: `CONV:${arg.conversationId}` }],
    }),
    sendMessage: build.mutation<InboxMessage, SendMessageArgs>({
      // `replyTo` é só da bolha otimista; o servidor recebe o id.
      query: ({ conversationId, replyTo: _replyTo, ...body }) => ({
        url: `/conversations/${conversationId}/messages`,
        method: 'POST',
        body,
      }),
      // Optimistic update — msg aparece PENDING imediatamente; rollback se falhar.
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const optimistic = mensagemOtimista(arg, getState, {
          type: arg.type ?? 'TEXT',
          text: arg.text ?? null,
          mediaUrl: arg.mediaUrl ?? null,
          templateName: arg.templateName ?? null,
        });
        await envioOtimista(dispatch, queryFulfilled, optimistic);
      },
    }),
    // Envio de mídia (imagem/áudio/arquivo) — multipart/form-data. O endpoint JSON
    // de /messages cobre só TEXT/TEMPLATE; mídia tem rota dedicada que sobe o binário.
    sendMediaMessage: build.mutation<InboxMessage, SendMediaArgs>({
      query: ({
        conversationId,
        file,
        filename,
        type,
        clientMessageId,
        caption,
        durationSec,
        replyToMessageId,
      }) => {
        const form = new FormData();
        form.append('file', file, filename);
        form.append('type', type);
        form.append('clientMessageId', clientMessageId);
        if (caption) form.append('caption', caption);
        if (durationSec != null) form.append('durationSec', String(durationSec));
        if (replyToMessageId) form.append('replyToMessageId', replyToMessageId);
        return {
          url: `/conversations/${conversationId}/messages/media`,
          method: 'POST',
          body: form,
        };
      },
      // Optimistic: bolha PENDING com preview local (object URL) trocada pela Message
      // do servidor (mediaUrl = proxy) no mesmo clientMessageId; rollback se falhar.
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const previewUrl = URL.createObjectURL(arg.file);
        const optimistic = mensagemOtimista(arg, getState, {
          type: arg.type,
          text: arg.caption ?? null,
          mediaUrl: previewUrl,
          metadata: {
            mimeType: arg.file.type || undefined,
            filename: arg.filename,
            size: arg.file.size,
            durationSec: arg.durationSec,
          },
        });
        try {
          await envioOtimista(dispatch, queryFulfilled, optimistic);
        } finally {
          // Server message já traz o proxy; o preview local não é mais referenciado.
          URL.revokeObjectURL(previewUrl);
        }
      },
    }),
    // Cartão de contato (menu Anexar > Contato). O back monta o `formatted_name` com o
    // número formatado quando o nome vem vazio, porque a Meta exige o campo.
    sendContactsMessage: build.mutation<InboxMessage, SendContactsArgs>({
      query: ({ conversationId, clientMessageId, contacts, replyToMessageId }) => ({
        url: `/conversations/${conversationId}/messages/contacts`,
        method: 'POST',
        body: {
          clientMessageId,
          contacts,
          ...(replyToMessageId ? { replyToMessageId } : {}),
        },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const optimistic = mensagemOtimista(arg, getState, {
          type: 'CONTACTS',
          text: arg.contacts.map((c) => c.name?.trim() || c.phone).join(', '),
          metadata: {
            contacts: arg.contacts.map((c) => ({
              name: c.name?.trim() || c.phone,
              phones: [{ phone: c.phone, waId: null, type: null }],
              emails: [],
              org: null,
            })),
          },
        });
        await envioOtimista(dispatch, queryFulfilled, optimistic);
      },
    }),
    // Figurinha: JSON quando reaproveita uma recente da loja, multipart quando sobe um
    // arquivo (o back converte para webp 512x512).
    sendStickerMessage: build.mutation<InboxMessage, SendStickerArgs>({
      query: (arg) => {
        const url = `/conversations/${arg.conversationId}/messages/sticker`;
        if ('stickerMessageId' in arg) {
          return {
            url,
            method: 'POST',
            body: {
              clientMessageId: arg.clientMessageId,
              stickerMessageId: arg.stickerMessageId,
              ...(arg.replyToMessageId ? { replyToMessageId: arg.replyToMessageId } : {}),
            },
          };
        }
        const form = new FormData();
        form.append('file', arg.file, arg.file.name);
        form.append('clientMessageId', arg.clientMessageId);
        if (arg.replyToMessageId) form.append('replyToMessageId', arg.replyToMessageId);
        return { url, method: 'POST', body: form };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const localUrl = 'file' in arg ? URL.createObjectURL(arg.file) : null;
        const optimistic = mensagemOtimista(arg, getState, {
          type: 'STICKER',
          mediaUrl: 'previewUrl' in arg ? arg.previewUrl : localUrl,
          metadata: { mimeType: 'image/webp', animated: false },
        });
        try {
          await envioOtimista(dispatch, queryFulfilled, optimistic);
        } finally {
          if (localUrl) URL.revokeObjectURL(localUrl);
        }
      },
    }),
    // Reação da LOJA (uma por mensagem; a nova substitui, '' remove). Otimista: o chip
    // muda na hora e volta se o envio falhar. O WS `message.reactions.updated` confirma.
    setMessageReaction: build.mutation<
      { messageId: string; reactions: MessageReaction[] },
      SetReactionArgs
    >({
      query: ({ conversationId, messageId, emoji }) => ({
        url: `/conversations/${conversationId}/messages/${messageId}/reaction`,
        method: 'PUT',
        body: { emoji },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const eu = autorLogado(getState);
        const patch = dispatch(
          inboxApi.util.updateQueryData(
            'listMessages',
            { conversationId: arg.conversationId },
            (draft) => {
              const m = draft.items.find((it) => it.id === arg.messageId);
              if (!m) return;
              const others = (m.reactions ?? []).filter((r) => r.actor !== 'STORE');
              m.reactions = arg.emoji
                ? [...others, { actor: 'STORE', emoji: arg.emoji, authorId: eu?.id ?? null }]
                : others;
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            inboxApi.util.updateQueryData(
              'listMessages',
              { conversationId: arg.conversationId },
              (draft) => {
                const m = draft.items.find((it) => it.id === data.messageId);
                if (m) m.reactions = data.reactions;
              },
            ),
          );
        } catch {
          patch.undo();
        }
      },
    }),
    // Figurinhas distintas (por sha256) que já passaram pela loja, as 40 mais recentes.
    listRecentStickers: build.query<RecentSticker[], string>({
      query: (storeId) => `/stores/${storeId}/stickers/recent`,
      transformResponse: (raw: { data: RecentSticker[] }) => raw.data,
      providesTags: (_r, _e, storeId) => [{ type: 'Sticker' as const, id: storeId }],
    }),
    claimConversation: build.mutation<ConversationDetail, string>({
      query: (id) => ({ url: `/conversations/${id}/claim`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Conversation' as const, id },
        { type: 'Conversation' as const, id: 'LIST' },
        { type: 'Conversation' as const, id: 'QUEUE' },
      ],
    }),
    releaseConversation: build.mutation<ConversationDetail, string>({
      query: (id) => ({ url: `/conversations/${id}/release`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Conversation' as const, id },
        { type: 'Conversation' as const, id: 'LIST' },
        { type: 'Conversation' as const, id: 'QUEUE' },
      ],
    }),
    assignConversation: build.mutation<ConversationDetail, AssignArgs>({
      query: ({ conversationId, userId }) => ({
        url: `/conversations/${conversationId}/assign`,
        method: 'PATCH',
        body: { userId },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        { type: 'Conversation' as const, id: 'LIST' },
      ],
    }),
    updateConversationStatus: build.mutation<ConversationDetail, UpdateStatusArgs>({
      query: ({ conversationId, status, version, reason, lossReasonId }) => ({
        url: `/conversations/${conversationId}/status`,
        method: 'PATCH',
        body: {
          status,
          version,
          ...(reason ? { reason } : {}),
          ...(lossReasonId ? { lossReasonId } : {}),
        },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        { type: 'Conversation' as const, id: 'LIST' },
        // Concluir também tira o card da Fila (aba "Janela expirada"), onde a
        // conclusão agora pode ser disparada direto do card.
        { type: 'Conversation' as const, id: 'QUEUE' },
      ],
    }),
    createConversation: build.mutation<ConversationDetail, CreateConversationArgs>({
      query: (body) => ({ url: '/conversations', method: 'POST', body }),
      invalidatesTags: [{ type: 'Conversation' as const, id: 'LIST' }],
    }),
    toggleAutomation: build.mutation<ConversationDetail, ToggleAutomationArgs>({
      query: ({ conversationId, enabled }) => ({
        url: `/conversations/${conversationId}/automation`,
        method: 'PATCH',
        body: { enabled },
      }),
      // Optimistic — toggle responde instantâneo no UI.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          inboxApi.util.updateQueryData('getConversation', arg.conversationId, (draft) => {
            if (draft?.automation) draft.automation.enabled = arg.enabled;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_r, _e, arg) => [{ type: 'Conversation' as const, id: arg.conversationId }],
    }),
    // Marca a conversa como lida (zera unreadCount no backend + emite conversation.read).
    // Sem invalidação: o badge é sincronizado pela slice notifications (WS + local).
    markRead: build.mutation<{ conversationId: string; unreadCount: number }, string>({
      query: (conversationId) => ({
        url: `/conversations/${conversationId}/read`,
        method: 'POST',
      }),
    }),
    // Seed do badge de não-lidas no boot: conversas minhas com unreadCount > 0.
    unreadSummary: build.query<{ conversationId: string; unreadCount: number }[], void>({
      query: () => '/conversations/unread-summary',
    }),
    // Finalizar venda no chat: registra Pedido + Pagamento e conclui a conversa.
    // Invalida a lista + os KPIs (Metrics/RealtimeCounts) pra refletir receita/vendas.
    // RF-14: `valueCents` é OPCIONAL — quando há orçamento DRAFT o back usa
    // `quote.totalCents` (ignora valueCents); só enviamos o valor manual como fallback
    // quando não há orçamento.
    finalizeSale: build.mutation<
      ConversationDetail,
      {
        conversationId: string;
        valueCents?: number;
        method: 'PIX' | 'CARD' | 'CASH' | 'BOLETO';
        /** RF-05: motivo fixo 'SALE' — o back grava conclusionReason na conversa. */
        reason: Extract<ConclusionReason, 'SALE'>;
      }
    >({
      query: ({ conversationId, valueCents, method, reason }) => ({
        url: `/conversations/${conversationId}/sale`,
        method: 'POST',
        body: { ...(valueCents != null ? { valueCents } : {}), method, reason },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        { type: 'Conversation' as const, id: 'LIST' },
        { type: 'Conversation' as const, id: 'QUEUE' },
        'Metrics' as const,
        'RealtimeCounts' as const,
        'Leaderboard' as const,
      ],
    }),
    // RF-13: editar orçamento DRAFT enquanto o atendente responsável revisa a conversa.
    // Replace-set de itens; o back recomputa quote.totalCents. `version` = lock otimista.
    updateQuote: build.mutation<ConversationQuote, UpdateQuoteArgs>({
      query: ({ quoteId, version, items }) => ({
        url: `/quotes/${quoteId}`,
        method: 'PATCH',
        body: { version, items },
      }),
      // RF-16: invalida a conversa (painel) + dashboards (Metrics/Leaderboard) pra
      // refletir o novo total onde for exibido.
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        { type: 'Conversation' as const, id: 'LIST' },
        'Metrics' as const,
        'Leaderboard' as const,
      ],
    }),
    // RF-05: custo de IA estimado desta conversa (role ≥ GERENTE no back).
    // Read-only; `entries` é a rastreabilidade (task/modelo/tokens/custo por chamada).
    getConversationCost: build.query<ConversationCost, string>({
      query: (conversationId) => `/conversations/${conversationId}/cost`,
      // Tag própria (não `Conversation`) pra o refetch do custo ser disparado só pelo
      // listener de mensagem recebida — sem arrastar refetch de getConversation/eventos
      // a cada claim/release/status. O custo muda só quando a IA faz uma chamada LLM.
      providesTags: (_r, _e, id) => [{ type: 'ConversationCost' as const, id }],
    }),
    // RF-15: corrigir orçamento já concluído (APPROVED) — só GERENTE+. O back
    // propaga saleValueCents/Order/Payment/Contact + AuditLog sozinho (RF-16).
    updateConcludedQuote: build.mutation<ConversationQuote, UpdateQuoteArgs>({
      query: ({ quoteId, version, items }) => ({
        url: `/quotes/${quoteId}/concluded`,
        method: 'PATCH',
        body: { version, items },
      }),
      // RF-16: além da conversa, invalida KPIs/ranking/histórico financeiro pra os
      // dashboards refetcharem com o valor corrigido.
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Conversation' as const, id: arg.conversationId },
        { type: 'Conversation' as const, id: 'LIST' },
        'Metrics' as const,
        'Leaderboard' as const,
        'RealtimeCounts' as const,
        'SalesHistory' as const,
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListConversationsQuery,
  useGetConversationQuery,
  useListConversationEventsQuery,
  useGetConversationAnalysisQuery,
  useSummarizeConversationMutation,
  useGetTemplatesQuery,
  useGetStoreTemplatesQuery,
  useListMessagesQuery,
  useSendMessageMutation,
  useSendMediaMessageMutation,
  useSendContactsMessageMutation,
  useSendStickerMessageMutation,
  useSetMessageReactionMutation,
  useListRecentStickersQuery,
  useCreateConversationMutation,
  useClaimConversationMutation,
  useReleaseConversationMutation,
  useAssignConversationMutation,
  useUpdateConversationStatusMutation,
  useToggleAutomationMutation,
  useMarkReadMutation,
  useUnreadSummaryQuery,
  useFinalizeSaleMutation,
  useUpdateQuoteMutation,
  useUpdateConcludedQuoteMutation,
  useGetConversationCostQuery,
} = inboxApi;
